import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useGenerateWelcomeLettersMutation,
  useWelcomeLettersListQuery,
} from '@common/cache';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { WelcomeLettersColumnNames } from '@views/pages/DataAnalysis/components/WelcomeLetters/components';
import { formatApiError } from '@common/errors/error-formatting.ts';

export interface IWelcomeLettersTableEntries {
  id: string;
  organization_name: string;
  program_name: string;
  date: {
    from: string;
    to: string;
  };
  patients: number;
  download_date: string;
}

export interface IWelcomeLettersTableFilters {
  sort_by: WelcomeLettersColumnNames;
  sort_order: 'asc' | 'desc';
  page: number;
  limit: number;
  from: Dayjs;
  to: Dayjs;
}

const defaultFromDate = new Date();
defaultFromDate.setMonth(defaultFromDate.getMonth() - 1);

const initialFilter: IWelcomeLettersTableFilters = {
  sort_order: 'desc',
  sort_by: 'date',
  page: 1,
  limit: 100,
  from: dayjs(defaultFromDate),
  to: dayjs(new Date()),
};

export const useWelcomeLetters = ({
  programId,
  orgId,
  setOrgId,
  setProgramId,
}: {
  programId: string;
  orgId: string;
  setOrgId: (orgId: string) => void;
  setProgramId: (programId: string) => void;
}) => {
  const [filters, setFilters] =
    useState<IWelcomeLettersTableFilters>(initialFilter);

  const {
    data = {
      count: 0,
      batches: [],
      patients_ready_to_welcome: 0,
    },
    refetch,
    isFetching,
  } = useWelcomeLettersListQuery(programId, orgId, filters);

  const entries: IWelcomeLettersTableEntries[] = data.batches.map(letter => ({
    download_date: letter.download_date
      ? dayjs(letter.download_date).format('MMM DD,YYYY')
      : '-',
    id: letter.id,
    organization_name: letter.organization_name,
    program_name: letter.program_name,
    date: {
      from: letter.earliest_date,
      to: letter.latest_date,
    },
    patients: letter.patients,
  }));

  useEffect(() => {
    if (programId && orgId) {
      refetch();
    }
  }, [programId, orgId]);

  const handleChangeOrgId = (event: SelectChangeEvent<string>) => {
    setOrgId(event.target.value);
  };

  const handleChangeProgramId = (event: SelectChangeEvent<string>) => {
    setProgramId(event.target.value);
  };

  const { mutateAsync, isPending } = useGenerateWelcomeLettersMutation(
    programId,
    orgId,
  );

  const handleGenerateLetters = async () => {
    try {
      await mutateAsync();

      toast.success('New file generated!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    entries,
    totalCount: data.count,
    filters,
    setFilters,
    isLoading: isFetching,
    patients_ready_to_welcome: data.patients_ready_to_welcome,
    handleChangeOrgId,
    handleChangeProgramId,
    handleGenerateLetters,
    generateIsPending: isPending,
  };
};
